import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "./contexts/AuthContext";
import { UserProvider } from "./contexts/UserContext";
import { HomeContentProvider } from "./contexts/HomeContentContext";
import { ToastContainer } from "./components/Toast";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { usePageTracking } from "./utils/analytics";
import { CommunityProvider } from "./contexts/CommunityContext";
import { EventProvider } from "./contexts/EventContext";
import { PlayerProvider } from "./contexts/PlayerContext";
import { NewsProvider } from "./contexts/NewsContext";
import { ChatProvider } from "./contexts/ChatContext";
// import LandscapeScene from './components/LandscapeScene';
// Pages
// Pages - Lazy loaded for code splitting
const HomePage = React.lazy(() => import("./pages/HomePage"));
const NewsPage = React.lazy(() => import("./pages/NewsPage"));
const NewsArticlePage = React.lazy(() => import("./pages/NewsArticlePage"));
const DiscoverPage = React.lazy(() => import("./pages/DiscoverPage"));
const CollaboratePage = React.lazy(() => import("./pages/CollaboratePage"));
const CommunityPage = React.lazy(() => import("./pages/CommunityPage"));
const EventPage = React.lazy(() => import("./pages/EventPage"));
const ArtistsPage = React.lazy(() => import("./pages/ArtistsPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const ArtistRegisterPage = React.lazy(
  () => import("./pages/ArtistRegisterPage"),
);
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
const ArtistProfile = React.lazy(() => import("./pages/ArtistProfile"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const ManagementDashboard = React.lazy(() => import("./pages/ManagementDashboard"));
const DiscussionPage = React.lazy(() => import("./pages/DiscussionPage"));
const ChatPage = React.lazy(() => import("./pages/ChatPage"));
const TracksPage = React.lazy(() => import("./pages/TracksPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const DMCAPage = React.lazy(() => import("./pages/DMCAPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const CookiePolicyPage = React.lazy(() => import("./pages/CookiePolicyPage"));
const HelpCenterPage = React.lazy(() => import("./pages/HelpCenterPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const IntegrationsCallbackPage = React.lazy(
  () => import("./pages/IntegrationsCallbackPage"),
);
const EventsListPage = React.lazy(() => import("./pages/EventsListPage"));
const JamPage = React.lazy(() => import("./pages/JamPage"));
const JamSessionRecap = React.lazy(
  () => import("./pages/JamSessionRecap"),
);
import MusicPlayer from "./components/MusicPlayer";
import { ExternalPlayerBridge } from "./components/integrations/ExternalPlayerBridge";
import PageTransition from "./components/PageTransition";
import ScrollToTop from "./components/ScrollToTop";

const AppRoutes = () => {
  const location = useLocation();
  usePageTracking();

  return (
    <div className="min-h-screen bg-background-dark text-white font-sans overflow-x-hidden antialiased selection:bg-gold/30 selection:text-gold">
      <ScrollToTop />
      <Navbar />

      {/* Background elements */}
      <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-gold/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[30%] h-[30%] rounded-full bg-primary/10 blur-[100px]" />
      </div>

      <main className="pt-20 pb-24 md:pb-0">
        <AnimatePresence mode="wait">
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
              </div>
            }
          >
            <Routes location={location} key={location.pathname}>
              <Route
                path="/"
                element={
                  <PageTransition>
                    <HomePage />
                  </PageTransition>
                }
              />
              <Route
                path="/discover"
                element={
                  <PageTransition>
                    <DiscoverPage />
                  </PageTransition>
                }
              />
              <Route
                path="/community"
                element={
                  <PageTransition>
                    <CommunityPage />
                  </PageTransition>
                }
              />
              <Route
                path="/events"
                element={
                  <PageTransition>
                    <EventsListPage />
                  </PageTransition>
                }
              />
              <Route
                path="/events/:id"
                element={
                  <PageTransition>
                    <EventPage />
                  </PageTransition>
                }
              />
              <Route
                path="/collaborate"
                element={
                  <PageTransition>
                    <CollaboratePage />
                  </PageTransition>
                }
              />
              <Route
                path="/jam/:roomId?"
                element={
                  <PageTransition>
                    <JamPage />
                  </PageTransition>
                }
              />
              <Route
                path="/jam/sessions/:roomId"
                element={
                  <PageTransition>
                    <JamSessionRecap />
                  </PageTransition>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PageTransition>
                    <ManagementDashboard />
                  </PageTransition>
                }
              />
              <Route
                path="/admin"
                element={
                  <PageTransition>
                    <ManagementDashboard />
                  </PageTransition>
                }
              />
              <Route
                path="/news"
                element={
                  <PageTransition>
                    <NewsPage />
                  </PageTransition>
                }
              />
              <Route
                path="/news/:slug"
                element={
                  <PageTransition>
                    <NewsArticlePage />
                  </PageTransition>
                }
              />
              <Route
                path="/artists"
                element={
                  <PageTransition>
                    <ArtistsPage />
                  </PageTransition>
                }
              />
              <Route
                path="/artist/:slug"
                element={
                  <PageTransition>
                    <ArtistProfile />
                  </PageTransition>
                }
              />
              <Route
                path="/login"
                element={
                  <PageTransition>
                    <LoginPage />
                  </PageTransition>
                }
              />
              <Route
                path="/register"
                element={
                  <PageTransition>
                    <RegisterPage />
                  </PageTransition>
                }
              />
              <Route
                path="/register/artist"
                element={
                  <PageTransition>
                    <ArtistRegisterPage />
                  </PageTransition>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <PageTransition>
                    <VerifyEmailPage />
                  </PageTransition>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PageTransition>
                    <ForgotPasswordPage />
                  </PageTransition>
                }
              />
              <Route
                path="/profile"
                element={
                  <PageTransition>
                    <ProfilePage />
                  </PageTransition>
                }
              />
              <Route
                path="/settings/integrations/callback"
                element={
                  <PageTransition>
                    <IntegrationsCallbackPage />
                  </PageTransition>
                }
              />
              <Route
                path="/community/discussion/:id"
                element={
                  <PageTransition>
                    <DiscussionPage />
                  </PageTransition>
                }
              />
              <Route
                path="/messages"
                element={
                  <PageTransition>
                    <ChatPage />
                  </PageTransition>
                }
              />
              <Route
                path="/tracks"
                element={
                  <PageTransition>
                    <TracksPage />
                  </PageTransition>
                }
              />
              <Route
                path="/terms"
                element={
                  <PageTransition>
                    <TermsPage />
                  </PageTransition>
                }
              />
              <Route
                path="/privacy"
                element={
                  <PageTransition>
                    <PrivacyPage />
                  </PageTransition>
                }
              />
              <Route
                path="/cookie-policy"
                element={
                  <PageTransition>
                    <CookiePolicyPage />
                  </PageTransition>
                }
              />
              <Route
                path="/help"
                element={
                  <PageTransition>
                    <HelpCenterPage />
                  </PageTransition>
                }
              />
              <Route
                path="/contact"
                element={
                  <PageTransition>
                    <ContactPage />
                  </PageTransition>
                }
              />
              <Route
                path="/about"
                element={
                  <PageTransition>
                    <AboutPage />
                  </PageTransition>
                }
              />
              <Route
                path="/dmca"
                element={
                  <PageTransition>
                    <DMCAPage />
                  </PageTransition>
                }
              />
            </Routes>
          </React.Suspense>
        </AnimatePresence>
      </main>

      {/* Main UI Components */}
      <Footer />
      <MusicPlayer />
      <ExternalPlayerBridge />
      <ToastContainer position="top-right">{null}</ToastContainer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      {/* Removed ParticleBackground component */}

      <AuthProvider>
        <UserProvider>
          <HomeContentProvider>
            <CommunityProvider>
              <EventProvider>
                <NewsProvider>
                  <ChatProvider>
                    <PlayerProvider>
                      <AppRoutes />
                    </PlayerProvider>
                  </ChatProvider>
                </NewsProvider>
              </EventProvider>
            </CommunityProvider>
          </HomeContentProvider>
        </UserProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
